import { format } from 'date-fns';
import { globals } from './globals';

/**
 * Calculates the estimated delivery date based on the current date and standard delivery days.
 * @param {number} standardDeliveryDays - The number of days for standard delivery (default is 3).
 * @returns {string} - The formatted estimated delivery date.
 */

/**
 * Gets the correct ordinal suffix (st, nd, rd, th) for the given day string.
 * @param {string} day - The day string.
 * @returns {string} - The correct ordinal suffix.
 */
const getDaySuffix = (day: string): string => {
  if (day.endsWith('11') || day.endsWith('12') || day.endsWith('13')) {
    return 'th';
  }
  if (day.endsWith('1')) {
    return 'st';
  }
  if (day.endsWith('2')) {
    return 'nd';
  }
  if (day.endsWith('3')) {
    return 'rd';
  }
  return 'th';
};

export const getDayWithSuffix = (date: Date): string => {
  const day = format(date, 'd');
  const suffix = getDaySuffix(day);
  return `${day}${suffix}`;
};

/**
 * Adds a number of days to a date, skipping Sundays.
 * @param {Date} date - The start date.
 * @param {number} daysToAdd - Number of days to add, excluding Sundays.
 * @returns {Date} - The resulting date after adding days.
 */
export const addDaysExcludingSundays = (date: Date, daysToAdd: number): Date => {
  const result = new Date(date);
  let addedDays = 0;

  while (addedDays < daysToAdd) {
    result.setDate(result.getDate() + 1);
    if (result.getDay() !== 0) {
      addedDays++;
    }
  }

  return result;
};

/**
 * Calculates the estimated delivery date range by adding a standard number of delivery days
 * to the current date while excluding Sundays.
 *
 * - If the start and end dates fall within the same month, the format will be: `startDay - endDay Month`.
 * - If the dates cross months, the format will be: `startDay StartMonth - endDay EndMonth`.
 *
 * If the user's country is 'us' (from the `globals` object), it will instead format the output
 * as a full date range (e.g., `Apr 25, 2025 - Apr 26, 2025`).
 *
 * @param {number} [standardDeliveryDays=3] - The number of days to add to the current date to calculate the delivery start date, excluding Sundays. Defaults to 3.
 * @returns {string} - A formatted string representing the estimated delivery date range.
 */
export function calculateEstimatedDeliveryDate(standardDeliveryDays = 3): string {
  const startDate = addDaysExcludingSundays(new Date(), standardDeliveryDays);
  const endDate = addDaysExcludingSundays(startDate, 1);

  const startDay = getDayWithSuffix(startDate);
  const endDay = getDayWithSuffix(endDate);

  const startMonth = format(startDate, 'MMM');
  const endMonth = format(endDate, 'MMM');
  const delivery_date_range =
    startMonth === endMonth
      ? `${startDay} - ${endDay} ${endMonth}`
      : `${startDay} ${startMonth} - ${endDay} ${endMonth}`;

  return globals.country === 'us'
    ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
    : delivery_date_range;
}

/**
 * Get the count down timer message from the given date string.
 * @param {string} dateString - The date string of datetime when the count down ends.
 * @returns {string} - The formatted count downtimer message. (e.g. "1 day : 3 hours : 5 minutes" )
 */
export function getCountDownTime(dateString: string): string {
  let text = '';
  const time = Date.parse(dateString) - Date.now();
  const d = Math.floor(time / (1000 * 60 * 60 * 24));
  const h = Math.floor((time / (1000 * 60 * 60)) % 24);
  const m = Math.floor((time / 1000 / 60) % 60);
  if (d > 0) {
    text += `${d} day${d !== 1 ? 's' : ''} : `;
  }
  if (d > 0 || h > 0) {
    text += `${h} hour${h !== 1 ? 's' : ''} : `;
  }
  if (d > 0 || h > 0 || m > 0) {
    text += `${m} minute${m !== 1 ? 's' : ''}`;
  }
  return text;
}
/**
 * Verifies that start and end date are within today.
 * @param {string} startDate - The start date string of datetime.
 * @param {string} endDate - The end date string of datetime.
 * @returns {boolean} - Verifies that today is between start and end date.
 */
/**
 * Safari-compatible date parsing function
 * Converts formats like "2025-Nov-14 00:00:00" to Safari-compatible format
 * Returns null for invalid dates instead of a fallback date
 */
const parseSafariCompatibleDate = (dateString: string): Date | null => {
  // Handle empty or non-string inputs
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  // If it's already a valid date, return it
  let date = new Date(dateString);
  if (!Number.isNaN(date.getTime())) {
    return date;
  }

  // Handle format like "2025-Nov-14 00:00:00"
  // Convert month names to numbers for Safari compatibility
  const monthMap: { [key: string]: string } = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
  };

  // Try to parse common problematic formats
  const monthNamePattern = /(\d{4})-([A-Za-z]{3})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/;
  const match = monthNamePattern.exec(dateString);

  if (match) {
    const [, year, monthName, day, hour, minute, second] = match;
    const monthNumber = monthMap[monthName];

    if (monthNumber) {
      // Create ISO format: YYYY-MM-DDTHH:mm:ss
      const isoFormat = `${year}-${monthNumber}-${day}T${hour}:${minute}:${second}`;
      date = new Date(isoFormat);

      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }
  }

  // If all parsing fails, return null to indicate parsing failure
  return null;
};

export const isInDateRange = (startDate: string, endDate: string): boolean => {
  const today = new Date();
  const start = parseSafariCompatibleDate(startDate);
  const end = parseSafariCompatibleDate(endDate);

  // Check if parsing failed (null returned)
  if (!start || !end) {
    return false;
  }

  // Validate that dates are valid Date objects
  const isStartValid = !Number.isNaN(start.getTime());
  const isEndValid = !Number.isNaN(end.getTime());

  if (!isStartValid || !isEndValid) {
    return false;
  }

  return today >= start && today <= end;
}; // Pluralizes a word based on the given count.
export function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`;
}
